import { Grid, Paper, Slider, Stack, Typography } from "@mui/material";
import { Profile } from "@readyfit-common/models";
import { useEffect, useState } from "react";
import AthleteIcon from "src/grading/components/AthleteIcon/AthleteIcon";
import GradingHeader from "src/grading/components/GradingHeader/GradingHeader";
import LiveTestStepper from "src/grading/components/LiveTestStepper/LiveTestStepper";
import { GradeResult } from "src/grading/models";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  LiveTestStep,
  selectCurrentLiveTest,
  selectLiveTestGradeResults,
  selectLiveTestStep,
  selectSelectedAthletes,
  setAthletesLiveTestGradeResults,
} from "src/store/reducers/liveTest/liveTestSlice";
import theme from "src/theme";

interface BodyCompStepProps {
  users: Profile[];
  setCanNavigateAway: React.Dispatch<React.SetStateAction<boolean>>;
}

interface AthleteBodyComp {
  height: string;
  waistCircumference: string;
}

const BodyCompStep = ({ users, setCanNavigateAway }: BodyCompStepProps) => {
  const dispatch = useAppDispatch();
  const liveTest = useAppSelector(selectCurrentLiveTest);
  const liveTestStep = useAppSelector(selectLiveTestStep);
  const selectedAthletes = useAppSelector(selectSelectedAthletes);
  const gradeResults = useAppSelector(selectLiveTestGradeResults);

  const [athleteBodyComps, setAthleteBodyComps] = useState<
    Record<string, AthleteBodyComp>
  >({});

  // Initialize body comp values from athlete profiles
  useEffect(() => {
    const initial: Record<string, AthleteBodyComp> = {};
    selectedAthletes.forEach((athlete) => {
      initial[athlete.id] = {
        height: athlete.height || "",
        waistCircumference: athlete.waistCircumference || "",
      };
    });
    setAthleteBodyComps(initial);
  }, [selectedAthletes]);

  // Store body comp grade results when values change
  useEffect(() => {
    if (Object.keys(athleteBodyComps).length === 0) return;

    const stepVal = liveTestStep - LiveTestStep.GRADING_STEPS;
    let results = gradeResults.slice();

    selectedAthletes.forEach((athlete) => {
      const bodyComp = athleteBodyComps[athlete.id];
      if (!bodyComp) return;

      const heightVal = parseFloat(bodyComp.height);
      const waistVal = parseFloat(bodyComp.waistCircumference);
      const whtr =
        !isNaN(heightVal) && !isNaN(waistVal) && heightVal > 0
          ? Math.round((waistVal / heightVal) * 100) / 100
          : undefined;

      const gradeResult: GradeResult = {
        discrepancies: [],
        notes: "",
        exceededRestTime: false,
        userResult: {
          startTime: Date.now(),
          endTime: Date.now(),
          exerciseId: "airforce-bodycomp",
          name: "Body Composition",
          height: bodyComp.height,
          waistCircumference: bodyComp.waistCircumference,
          whtr,
          exerciseCategory: "WHtR",
        },
      };

      const athleteIndex = results.findIndex(
        (r) => r.profile.id === athlete.id,
      );

      if (athleteIndex === -1) {
        results = [
          ...results,
          {
            gradeResults: [gradeResult],
            profile: athlete,
            athleteSignatureDetails: {
              agreed: false,
              disagreementReason: "",
              signature: "",
            },
            graderSignatureDetails: {
              agreed: false,
              disagreementReason: "",
              signature: "",
            },
          },
        ];
      } else {
        const newGradeResults = results[athleteIndex].gradeResults.slice();
        newGradeResults[stepVal] = gradeResult;
        results = results.map((r, i) =>
          i === athleteIndex ? { ...r, gradeResults: newGradeResults } : r,
        );
      }
    });

    dispatch(
      setAthletesLiveTestGradeResults({
        athleteLiveTestGradeResults: results,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteBodyComps]);

  const handleChange = (
    athleteId: string,
    field: "height" | "waistCircumference",
    value: string,
  ) => {
    setAthleteBodyComps((prev) => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        [field]: value,
      },
    }));
  };

  return (
    <Grid container position={"relative"}>
      <Grid
        container
        component={Paper}
        elevation={1}
        style={{
          padding: theme.spacing(2),
          paddingBottom: theme.spacing(1),
        }}
      >
        <Grid item container xs={12}>
          <GradingHeader
            withBreadcrumbs
            containerStyles={{
              marginTop: 0,
              marginBottom: theme.spacing(2),
            }}
          />
          <Typography variant="h3">Body Composition</Typography>

          <Typography
            variant="subtitle1"
            style={{
              textTransform: "uppercase",
              backgroundColor: "#D6D6D6",
              color: "#333333",
              borderRadius: "5px",
              marginLeft: theme.spacing(2),
              display: "flex",
              padding: `0 ${theme.spacing(1)}`,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Body Composition
          </Typography>

          <Grid item marginLeft={"auto"} marginRight={1}>
            <LiveTestStepper
              athletes={users}
              setCanNavigateAway={setCanNavigateAway}
              mini={true}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item container xs={12} style={{ marginTop: theme.spacing(4) }}>
        {selectedAthletes.map((athlete) => {
          const bodyComp = athleteBodyComps[athlete.id];
          return (
            <Grid
              key={athlete.id}
              item
              container
              xs={12}
              style={{
                border: `1px solid ${theme.palette.secondary.light}`,
                borderRadius: theme.spacing(1),
                marginBottom: theme.spacing(1),
                padding: theme.spacing(2),
                alignItems: "center",
              }}
              gap={2}
            >
              <Grid
                item
                container
                xs={3}
                alignItems={"center"}
                style={{ gap: theme.spacing(2) }}
              >
                <AthleteIcon profile={athlete} />
                <Typography variant="h4">
                  {athlete.firstName} {athlete.lastName}
                </Typography>
              </Grid>

              <Grid item container flex={1} spacing={2}>
                <Grid item xs={5}>
                  <Typography variant="body2" color="textSecondary">
                    Height (inches)
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Slider
                      size="small"
                      value={bodyComp?.height ? +bodyComp.height : 55}
                      onChange={(_, val) =>
                        handleChange(athlete.id, "height", String(val))
                      }
                      min={55}
                      max={80}
                      step={0.5}
                    />
                    <Typography variant="subtitle1" sx={{ minWidth: 50 }}>
                      {bodyComp?.height || "55"} in
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2" color="textSecondary">
                    Waist (inches)
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Slider
                      size="small"
                      value={
                        bodyComp?.waistCircumference
                          ? +bodyComp.waistCircumference
                          : 20
                      }
                      onChange={(_, val) =>
                        handleChange(
                          athlete.id,
                          "waistCircumference",
                          String(val),
                        )
                      }
                      min={20}
                      max={70}
                      step={0.5}
                    />
                    <Typography variant="subtitle1" sx={{ minWidth: 50 }}>
                      {bodyComp?.waistCircumference || "20"} in
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default BodyCompStep;
